import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PRODUCTION_CENTER_TYPES,
  useApi,
  type ProductionCenterDTO,
} from "../../api";
import { CENTER_TYPE_VARIANT } from "../../components/badges";
import { EnumBadge } from "../../components/cells";
import { crudEditor, multiSelectField, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * Production centres and what they can make.
 *
 * The two set columns are what dispatch routes on: a centre is offered for an
 * order line only when it ships to the destination country *and* carries the
 * ordered product, which is why both are edited inline rather than on a
 * separate screen.
 */
export function ProductionCentersPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.PRODUCTION_CENTER);
  const title = t("navigation.item.production-centers", "Production centers");

  const { data, isLoading, error } = useQuery({
    queryKey: ["production", "center"],
    queryFn: () => api.productionCenters.findAll(),
    enabled: allowed,
  });

  const countries = useQuery({
    queryKey: ["country"],
    queryFn: () => api.countries.findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  const products = useQuery({
    queryKey: ["product"],
    queryFn: () => api.products.findAll(),
    enabled: allowed,
    staleTime: 5 * 60_000,
  });

  const rows = data ?? [];
  const countryList = countries.data ?? [];
  const productList = products.data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["production", "center"] });

  const cols = useMemo(() => {
    const c = defineColumns<ProductionCenterDTO>();
    return [
      c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
      c.custom(
        "type",
        (row) => row.type,
        (row) => <EnumBadge value={row.type} variants={CENTER_TYPE_VARIANT} />,
        { headerKey: "column.type", filter: "select", priority: 2 },
      ),
      c.number("countries", (row) => row.countries?.length ?? 0, {
        headerKey: "column.countries",
        priority: 3,
      }),
      c.number("products", (row) => row.products?.length ?? 0, {
        headerKey: "column.products",
        priority: 3,
      }),
    ];
  }, []);

  const editor = useMemo(
    () =>
      crudEditor<ProductionCenterDTO>({
        client: api.productionCenters,
        refresh,
        t,
        canDelete: false,
        fields: {
          name: textField(
            (row) => row.name,
            (row, value) => ({ ...row, name: value }),
            true,
          ),
          type: selectField(
            (row) => row.type,
            (row, value) => ({ ...row, type: value as ProductionCenterDTO["type"] }),
            PRODUCTION_CENTER_TYPES,
          ),
          countries: multiSelectField(
            (row) => (row.countries ?? []).map((entry) => String(entry.id)),
            (row, values) => ({
              ...row,
              countries: countryList.filter((entry) => values.includes(String(entry.id))),
            }),
            countryList.map((entry) => ({
              value: String(entry.id),
              label: `${entry.geozone} - ${entry.name}`,
            })),
          ),
          products: multiSelectField(
            (row) => (row.products ?? []).map((entry) => String(entry.id)),
            (row, values) => ({
              ...row,
              products: productList.filter((entry) => values.includes(String(entry.id))),
            }),
            productList.map((entry) => ({
              value: String(entry.id),
              label: `${entry.category?.name ?? ""} - ${entry.code}`,
            })),
          ),
        },
        newRow: () => ({
          id: 0,
          name: "",
          type: "EXTERNAL",
          priority: 0,
          countries: [],
          products: [],
        }),
        validate: (row) => {
          if (!row.name.trim()) return { name: t("form.validation.field.required", "Value is required") };
          if (rows.some((entry) => entry.id !== row.id && entry.name === row.name)) {
            return { name: t("form.validation.field.unique", "Value already exists") };
          }
          return null;
        },
      }),
    [api, countryList, productList, rows, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<ProductionCenterDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        editor={editor}
      />
    </>
  );
}
